import { Directive, inject, input } from '@angular/core';
import { Router } from '@angular/router';

import { AnalyticsService } from '../services/analytics.service';

/**
 * A path inside this app: one leading slash, and not a protocol-relative `//`
 * URL, which points at another host however much it looks like a path.
 *
 * Fragment-only links (`#section`) are left out deliberately — those are
 * in-document jumps the browser already handles better than the router would.
 */
const INTERNAL_PATH = /^\/(?!\/)/;

/**
 * Makes in-app links inside rendered assistant markdown navigate as navigation.
 *
 * Markup from `MarkdownPipe` arrives through `[innerHTML]`, which Angular never
 * compiles — so its anchors can carry no `routerLink`, no `faroAction`, and no
 * binding of their own. Left alone, clicking one is a full document load: the
 * SPA boots again and the conversation the reader was in is gone.
 *
 * So the container listens instead. Two kinds of link reach it:
 *
 *  - offering links the pipe generated, identified by `data-offering-slug`,
 *    which carries what a `routerLink` normally would;
 *  - any other path-relative link, which in practice means one the model wrote
 *    into its answer.
 *
 * External URLs are not touched: the renderer sends those to a new tab, and a
 * new tab leaves the conversation standing. One listener serves every link in
 * every answer under it.
 *
 * Put it on whatever element wraps the rendered markdown:
 *
 * ```html
 * <div inexpOfferingLinks="assistant transcript">…</div>
 * ```
 */
@Directive({
  selector: '[inexpOfferingLinks]',
  host: {
    '(click)': 'onClick($event)',
  },
})
export class OfferingLinkDirective {
  private readonly _router = inject(Router);
  private readonly _analytics = inject(AnalyticsService);

  /** Where the link was, for telling the two assistant surfaces apart. */
  readonly surface = input<string>('assistant answer', {
    alias: 'inexpOfferingLinks',
  });

  onClick(event: MouseEvent): void {
    // A modified click is left entirely to the browser. Opening a link in a new
    // tab or window is the behaviour a router-driven handler most often breaks,
    // and it is exactly what someone comparing two offerings will reach for.
    if (
      event.button !== 0 ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    if (!(event.target instanceof Element)) {
      return;
    }
    const link = event.target.closest('a');
    if (!(link instanceof HTMLAnchorElement)) {
      return;
    }

    const slug = link.dataset['offeringSlug'];
    if (slug) {
      this._go(event, ['/offerings', slug], slug);
      return;
    }

    // Any other in-app path, which by now means a relative link the model
    // wrote — `[the catalogue](/search?q=x)`. Left alone these are a full
    // document load: the SPA boots again and the conversation is gone. They
    // reach here as bare same-tab anchors because the renderer only sends
    // genuinely external URLs to a new tab.
    const href = link.getAttribute('href');
    if (href && INTERNAL_PATH.test(href)) {
      this._go(event, href, href);
    }
  }

  /** Take over the click and route, recording it on the way. */
  private _go(
    event: MouseEvent,
    to: string | unknown[],
    detail: string,
  ): void {
    event.preventDefault();
    // `pushUserActionEvent` wraps `pushEvent`, never `startUserAction`: the
    // latter waits for the DOM to fall quiet for 100ms and never gets it on an
    // interaction that navigates and re-renders, which freezes the tab.
    this._analytics.pushUserActionEvent('assistant offering link', {
      surface: this.surface(),
      slug: detail,
    });
    void (Array.isArray(to)
      ? this._router.navigate(to)
      : this._router.navigateByUrl(to as string));
  }
}
